import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { TimezoneService } from '@sc/data-access-configuration';
import { TranslateModule } from '@ngx-translate/core';
import {
  Dossier,
  findCorrespondingServiceType,
  Fitter,
} from '@sc/data-access-models';
import { LocaleDatePipe } from '@sc/data-access-translation';
import { MomentParseZonePipe } from '../../pipes';
import { ButtonComponent } from '../button';
import { IconComponent } from '../icon';
import { dayjs } from '@sc/utils';
import { StorageService } from '@sc/data-access-storage';

@Component({
  selector: 'sc-overlapping-dossiers',
  styleUrls: ['./overlapping-dossiers.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    NgFor,
    NgClass,
    ButtonComponent,
    IconComponent,
    MomentParseZonePipe,
    LocaleDatePipe,
    AsyncPipe,
    TranslateModule,
  ],
  template: ` <div
    *ngIf="!!overlapDossiers?.length && !!fitter"
    class="closing-hours-alert-container"
  >
    <div
      *ngFor="let dossier of overlapDossiers"
      class="closing-hours-alert-item"
    >
      <div class="coverlap-dossier-container">
        <span
          class="coverlap-dossier-intervention-icon"
          [ngClass]="{
            'outside-intervention': isOutsideIntervention(dossier),
            'in-center-intervention': !isOutsideIntervention(dossier)
          }"
        >
          <sc-icon *ngIf="isOutsideIntervention(dossier)" name="home"></sc-icon>
          <sc-icon
            *ngIf="!isOutsideIntervention(dossier)"
            name="fitter"
          ></sc-icon>
        </span>

        <span class="coverlap-dossier-intervention-time">
          {{
            dossier.appointment?.time
              | parseZoneTimeString
                : timezone ?? timezoneService.getFitterTimezone(fitter)
              | localeDate: 'dd/MM'
              | async
          }}
          -
          {{
            dossier.appointment?.time
              | parseZoneTimeString
                : timezone ?? timezoneService.getFitterTimezone(fitter)
              | localeDate: 'shortTime'
              | async
          }}
        </span>
        <span class="coverlap-dossier-details" *ngIf="!isReducedWidth">
          <span class="coverlap-dossier-intervention-operation">
            {{ getInterventionName(dossier) }}
          </span>
          <span class="coverlap-dossier-intervention-name">
            {{ dossier.customer?.firstname }}
            {{ dossier.customer?.lastname }}
          </span>
          <span class="coverlap-dossier-intervention-plate">
            {{ dossier.claim?.car?.licensePlateNumber }}
          </span>
        </span>

        <span
          class="coverlap-dossier-intervention-redirect"
          *ngIf="!isBlockedByConcurency(dossier)"
        >
          <sc-button
            leftIcon="big_arrow_right"
            [iconOnly]="true"
            (click)="onOverlappingDossierClick(dossier)"
          ></sc-button>
        </span>

        <div
          *ngIf="isBlockedByConcurency(dossier) && showConcurrency"
          class="coverlap-dossier-concurrency"
        >
          <div class="infos-container">
            <span class="infos-title-warning"
              ><sc-icon name="alert"></sc-icon
              >{{
                'PlanningFormAppointment.workingOnDossierAlertTitle' | translate
              }}</span
            >
            <span
              [innerHTML]="
                'PlanningFormAppointment.workingOnDossierAlertInfos'
                  | translate: { userName: dossier.currentlyInUse?.userName }
              "
            ></span>
          </div>
        </div>
      </div>
      <span
        class="coverlap-dossier-details"
        [ngClass]="{
          'reduced-display': isReducedWidth
        }"
        *ngIf="isReducedWidth"
      >
        <span class="coverlap-dossier-intervention-operation">
          {{ getInterventionName(dossier) }}
        </span>
        <span class="coverlap-dossier-customer-infos">
          <span class="coverlap-dossier-intervention-name">
            {{ dossier.customer?.firstname }}
            {{ dossier.customer?.lastname }}
          </span>
          <span class="coverlap-dossier-intervention-plate">
            {{ dossier.claim?.car?.licensePlateNumber }}
          </span>
        </span>
      </span>
    </div>
  </div>`,
})
export class OverlappingDossiersComponent {
  @Input() overlapDossiers: Dossier[];
  @Input() fitter: Fitter;
  @Input() timezone?: string;
  @Input() isReducedWidth = false;
  @Input() showConcurrency = false;
  @Output() dossierClick = new EventEmitter<string>();
  constructor(
    public timezoneService: TimezoneService,
    private readonly storage: StorageService
  ) {}

  onOverlappingDossierClick(dossier: Dossier) {
    this.dossierClick.emit(dossier?.id);
  }

  isOutsideIntervention(dossier: Dossier) {
    return dossier?.appointment?.outsideIntervention;
  }

  getInterventionName(dossier: Dossier) {
    const foundServiceType = findCorrespondingServiceType(
      {
        id: dossier?.appointment?.interventionId,
        glassType: dossier?.claim?.glass?.glass,
        operationType: dossier?.claim?.operationType,
        isMobility: dossier?.appointment?.outsideIntervention,
        replacementVehicle: dossier?.appointment?.replacementVehicle,
        active: true,
        calibration: dossier?.claim?.calibration === 'true',
      },
      this.fitter?.serviceTypes
    );

    return foundServiceType?.name;
  }

  isBlockedByConcurency(dossier: Dossier) {
    const dossierOpenTTL = dayjs.duration(4, 'hours');
    const isOpenLockExpired = dayjs()
      .add(dossierOpenTTL.asMinutes(), 'minutes')
      .isBefore(dayjs(dossier?.currentlyInUse?.timestamp));

    return this.showConcurrency
      ? !!dossier?.currentlyInUse &&
          !isOpenLockExpired &&
          dossier.currentlyInUse.userId?.toLowerCase() !==
            this.storage.getItem('account-last-name', sessionStorage)
      : false;
  }
}
